import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Download, MessageCircle, Activity, Brain, Heart, IndianRupee, Briefcase, Home, Sparkles, Star,
  ArrowLeft, Loader2, TrendingUp, TrendingDown, CheckCircle2,
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { useTranslation } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SUPPORT_WHATSAPP_NUMBER = '918308595553';

// Dimension key → Lucide icon
const DIM_ICONS = {
  physical: Activity,
  mental: Brain,
  emotional: Heart,
  financial: IndianRupee,
  career: Briefcase,
  lifestyle: Home,
  self_care: Sparkles,
  spiritual: Star,
};

export default function Report() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/report/${slug}`);
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.status === 404 ? 'not_found' : 'error');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFB]" data-testid="report-loading">
        <Loader2 className="w-8 h-8 text-[#4F46E5] animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFB] p-6" data-testid="report-not-found">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {error === 'not_found' ? 'Report not found' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-[#64748B] mb-6">
            {error === 'not_found'
              ? 'This report link is invalid or has been removed.'
              : "We couldn't load your prescription. Please try again."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#4F46E5] font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const { personalization, pdf_url, template } = data;
  const radar = template.dimensions.map((d) => ({ dimension: d.label.replace(' Wellness', '').replace(' & Environmental', ''), score: d.score }));
  const overall = template.overall_score;
  const statusLabel = template.status_label;
  const statusColor = template.status_color;
  const pdfFullUrl = `${BACKEND_URL}${pdf_url}`;
  const reportUrl = data.report_url || `${window.location.origin}/report/${slug}`;
  const assessmentDate = (() => {
    try {
      return new Date(data.prescription?.generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (_) { return '—'; }
  })();

  const trackPdf = () => {
    try { axios.post(`${API}/analytics/track`, { event: 'pdf_downloaded', data: { slug } }); } catch (_) { /* noop */ }
  };
  const trackWA = (surface) => {
    try { axios.post(`${API}/analytics/track`, { event: 'whatsapp_clicked', data: { slug, surface } }); } catch (_) { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFB] to-[#F1F5F9] py-8 px-4 sm:px-6" data-testid="report-page">
      <div className="max-w-4xl mx-auto">

        {/* Brand bar */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="text-base font-bold text-[#0F172A] no-underline" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>
            Clinity
          </Link>
          <a
            href={pdfFullUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackPdf}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #9333EA 100%)', boxShadow: '0 8px 20px -8px rgba(79, 70, 229, 0.45)' }}
            data-testid="report-download-pdf"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>

        {/* ============ PAGE 1: EXECUTIVE SUMMARY ============ */}
        <Section testid="report-cover">
          <Kicker>Executive Wellness Summary</Kicker>
          <H1>{personalization?.name ? `${personalization.name}'s Wellness Prescription` : 'Your Wellness Prescription'}</H1>
          <p className="text-sm text-[#64748B] mb-6">Your Prescription for Complete Wellbeing™</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 text-sm">
            <MetaCell label="Name" value={personalization?.name || '—'} />
            <MetaCell label="Age" value={personalization?.age || '—'} />
            <MetaCell label="WhatsApp" value={personalization?.whatsapp || '—'} />
            <MetaCell label="Report ID" value={slug} />
            <MetaCell label="Assessment Date" value={assessmentDate} />
          </div>

          {/* Score banner */}
          <div
            className="rounded-2xl p-6 flex flex-col sm:flex-row items-center sm:items-stretch gap-6 text-white mb-8"
            style={{ background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}DD 100%)` }}
            data-testid="report-score-banner"
          >
            <div className="text-5xl sm:text-6xl font-bold leading-none">
              {overall}<span className="text-2xl opacity-80">/100</span>
            </div>
            <div className="text-center sm:text-left">
              <div className="text-[11px] font-bold uppercase tracking-wider opacity-90">Overall Wellness Score</div>
              <div className="text-2xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{statusLabel}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Wellness Wheel */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-4" data-testid="report-wheel">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2 px-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Wellness Wheel</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radar}>
                  <PolarGrid stroke="rgba(99, 102, 241, 0.15)" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fill: '#475569', fontSize: 11, fontWeight: 500 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#94A3B8', fontSize: 9 }} />
                  <Radar name="Score" dataKey="score" stroke="#4F46E5" fill="rgba(79, 70, 229, 0.2)" strokeWidth={2.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {/* Strengths + Improvements stacked */}
            <div className="space-y-3" data-testid="report-strengths-improvements">
              <StrengthImprovementBlock title="Top 3 Strength Areas" entries={template.strengths} accent="#10B981" icon={TrendingUp} />
              <StrengthImprovementBlock title="Top 3 Improvement Areas" entries={template.improvements} accent="#EF4444" icon={TrendingDown} />
            </div>
          </div>
        </Section>

        {/* ============ PAGES 2-9: DIMENSION PAGES ============ */}
        {template.dimensions.map((d) => (
          <DimensionSection key={d.key} dim={d} icon={DIM_ICONS[d.key] || Activity} />
        ))}

        {/* ============ PRE-CHALLENGE TRANSITION ============ */}
        <Section testid="report-pre-challenge">
          <Kicker>{template.pre_challenge.eyebrow}</Kicker>
          <H2 className="text-[#0F172A]">{template.pre_challenge.headline}</H2>
          <div className="space-y-3 mt-5">
            {template.pre_challenge.body.map((line, i) => (
              <p key={i} className="text-base text-[#475569] leading-relaxed">{line}</p>
            ))}
          </div>
          <p className="mt-6 text-center text-xl font-bold text-[#4F46E5]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {template.pre_challenge.cta}
          </p>
        </Section>

        {/* ============ 30-DAY CHALLENGE ============ */}
        <Section testid="report-challenge">
          <Kicker>30-Day Clinity Wellness Challenge</Kicker>
          <H1>{template.challenge.title}</H1>
          <p className="text-sm text-[#0F172A] mt-2 mb-1">{template.challenge.headline}</p>
          <p className="text-sm italic text-[#4F46E5] mb-6">{template.challenge.subhead}</p>

          {/* Checkbox grid 6×5 */}
          <div className="grid grid-cols-6 gap-3 max-w-md mx-auto mb-8">
            {Array.from({ length: 30 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg border-2 border-[#4F46E5] bg-white" />
            ))}
          </div>

          <div className="bg-slate-50 rounded-2xl p-5 mb-6">
            <p className="text-sm font-bold text-[#0F172A] mb-3">Before You Tick Today&rsquo;s Box, Ask Yourself:</p>
            <ul className="space-y-2">
              {template.challenge.questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#334155]">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-md mx-auto mb-6">
            <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#4F46E5]">Current</div>
              <div className="text-xl font-bold text-[#0F172A]">{overall}/100</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Target</div>
              <div className="text-xl font-bold text-emerald-700">{Math.min(100, overall + 15)}/100</div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Next</div>
              <div className="text-xs text-[#94A3B8] mt-1">30 days from today</div>
            </div>
          </div>

          <p className="text-center italic text-sm text-[#475569]">&ldquo;{template.challenge.footer_quote}&rdquo;</p>
          <p className="text-center text-xs text-[#94A3B8] mt-1">{template.challenge.footer_signature}</p>
        </Section>

        {/* ============ WHATSAPP FOLLOW-UP ============ */}
        <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-6 sm:p-8 my-6 text-center" data-testid="report-whatsapp-cta">
          <h3 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Need help implementing this?
          </h3>
          <p className="text-sm text-[#475569] mb-5">Talk to a Clinity Wellness Expert on WhatsApp.</p>
          <a
            href={`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Clinity team! I'd like help with my Wellness Prescription. Report: ${reportUrl}`)}`}
            target="_top"
            rel="noopener noreferrer"
            onClick={() => trackWA('report_page')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold transition-all hover:-translate-y-0.5 no-underline"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 14px 30px -10px rgba(37, 211, 102, 0.55)' }}
            data-testid="report-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" /> Open WhatsApp
          </a>
        </div>

        {/* ============ CLOSING NOTE ============ */}
        <Section testid="report-closing">
          <H1>{template.closing.title}</H1>
          <div className="space-y-4 mt-5">
            {template.closing.quotes.map((q, i) => (
              <p key={i} className="text-[15px] text-[#334155] leading-relaxed text-justify"
                 dangerouslySetInnerHTML={{ __html: q.replace(/&ldquo;/g, '“').replace(/&rdquo;/g, '”').replace(/&rsquo;/g, '’') }} />
            ))}
          </div>
          <div className="mt-8 space-y-1">
            {template.closing.signature_block.map((line, i) =>
              line === '' ? <div key={i} className="h-2" /> : (
                <p key={i} className="text-[15px] text-[#0F172A]">{line}</p>
              ),
            )}
          </div>
          <div className="mt-8 pt-4 border-t border-slate-200">
            <p className="text-[10px] font-bold text-[#64748B] mb-1">{template.closing.disclaimer_heading}</p>
            <p className="text-[10px] text-[#94A3B8] leading-relaxed text-justify">{template.closing.disclaimer_body}</p>
          </div>
        </Section>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function Section({ children, testid }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 mb-6 shadow-soft" data-testid={testid}>
      {children}
    </div>
  );
}
function Kicker({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4F46E5] mb-3">{children}</div>;
}
function H1({ children }) {
  return <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h1>;
}
function H2({ children, className = '' }) {
  return <h2 className={`text-xl sm:text-2xl font-bold ${className}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h2>;
}
function MetaCell({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-2 border border-slate-100">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#4F46E5]">{label}</div>
      <div className="text-sm text-[#0F172A] truncate" title={String(value)}>{value}</div>
    </div>
  );
}
function StrengthImprovementBlock({ title, entries, accent, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>{title}</div>
      </div>
      <div className="space-y-2">
        {entries.map((e) => (
          <div key={e.key} className="flex items-start gap-3 bg-slate-50 rounded-lg p-2.5">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#0F172A] truncate">{e.label}</div>
              <div className="text-[11px] text-[#64748B] leading-snug">{e.tagline}</div>
            </div>
            <div className="text-sm font-bold whitespace-nowrap" style={{ color: accent }}>{e.score}/100</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function DimensionSection({ dim, icon: Icon }) {
  return (
    <Section testid={`report-dim-${dim.key}`}>
      <div className="flex items-start gap-4 mb-5">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
             style={{ background: dim.status_color + '22' }}>
          <Icon className="w-7 h-7" style={{ color: dim.status_color }} />
        </div>
        <div className="flex-1">
          <Kicker>Clinity · {dim.label}</Kicker>
          <H1>{dim.label}</H1>
          <p className="text-sm text-[#64748B] mt-1">{dim.tagline}</p>
        </div>
        <div className="rounded-2xl px-4 py-3 text-center text-white shrink-0"
             style={{ background: `linear-gradient(135deg, ${dim.status_color} 0%, ${dim.status_color}DD 100%)` }}>
          <div className="text-3xl font-bold leading-none">{dim.score}<span className="text-base opacity-80">/100</span></div>
          <div className="text-[10px] font-bold uppercase tracking-wider mt-1">{dim.status_label}</div>
        </div>
      </div>

      <Kicker>Personalized Recommendations</Kicker>
      <ol className="space-y-3 mt-3 list-none">
        {dim.recommendations.map((rec, i) => {
          const [head, ...rest] = rec.split(':');
          const body = rest.join(':').trim();
          return (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-indigo-50 text-[#4F46E5] text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
              <div className="flex-1">
                <span className="text-sm font-bold text-[#0F172A]"
                      dangerouslySetInnerHTML={{ __html: head.replace(/&amp;/g, '&').replace(/&rsquo;/g, '’') }} />
                {body && <span className="text-sm text-[#475569]">: <span dangerouslySetInnerHTML={{ __html: body.replace(/&amp;/g, '&').replace(/&rsquo;/g, '’') }} /></span>}
              </div>
            </li>
          );
        })}
      </ol>

      {dim.advanced?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-slate-200">
          <Kicker>Advanced {dim.label} Strategies</Kicker>
          <div className="space-y-4 mt-3">
            {dim.advanced.map((b, i) => (
              <div key={i}>
                <div className="text-sm font-bold text-[#0F172A]"
                     dangerouslySetInnerHTML={{ __html: b.title.replace(/&amp;/g, '&') }} />
                <p className="text-sm text-[#475569] mt-1">{b.body.replace(/&amp;/g, '&')}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}
